/**
 * Playlists sagas
 */
import { call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import { httpRequest } from '../../services/http-service';
import { endPoints } from '../../services/endpoints';
import {
  fetchPlaylists,
  fetchPlaylist,
  createPlaylist,
  updatePlaylistAction,
  deletePlaylist,
  setPlaylists,
  setCurrentPlaylist,
  addPlaylist,
  updatePlaylist,
  removePlaylist,
  setLoading,
  setError,
} from './playlistsSlice';
import { presentLoader, dismissLoader } from '../../store/slices/commonSlice';

/**
 * Fetch all playlists saga
 */
function* fetchPlaylistsSaga(): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.playlists.getAll, {
      params: { page: 1, limit: 1000 },
    });

    if (response?.success) {
      yield put(setPlaylists(response.data || response));
    } else {
      toast.error('Failed to fetch playlists');
      yield put(setError('Failed to fetch playlists'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch playlists';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Fetch single playlist saga
 */
function* fetchPlaylistSaga(action: ReturnType<typeof fetchPlaylist>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.playlists.getById(action.payload));

    if (response?.success) {
      yield put(setCurrentPlaylist(response.data || response));
    } else {
      toast.error('Failed to fetch playlist');
      yield put(setError('Failed to fetch playlist'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch playlist';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Create playlist saga
 */
function* createPlaylistSaga(action: ReturnType<typeof createPlaylist>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.post, endPoints.playlists.create, action.payload);

    if (response?.success) {
      const newPlaylist = response.data || response;
      yield put(addPlaylist(newPlaylist));
    } else {
      toast.error('Failed to create playlist');
      yield put(setError('Failed to create playlist'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to create playlist';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Update playlist saga
 */
function* updatePlaylistSaga(action: ReturnType<typeof updatePlaylistAction>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.put,
      endPoints.playlists.update(action.payload.id),
      action.payload.data
    );

    if (response?.success) {
      const updatedPlaylist = { ...response.data, _id: action.payload.id };
      yield put(updatePlaylist(updatedPlaylist));
    } else {
      toast.error('Failed to update playlist');
      yield put(setError('Failed to update playlist'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to update playlist';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Delete playlist saga
 */
function* deletePlaylistSaga(action: ReturnType<typeof deletePlaylist>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.delete, endPoints.playlists.delete(action.payload));

    if (response?.success || response) {
      yield put(removePlaylist(action.payload));
    } else {
      toast.error('Failed to delete playlist');
      yield put(setError('Failed to delete playlist'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to delete playlist';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

export function* playlistsSagas(): Generator<any, void, any> {
  yield takeLatest(fetchPlaylists, fetchPlaylistsSaga);
  yield takeLatest(fetchPlaylist, fetchPlaylistSaga);
  yield takeLatest(createPlaylist, createPlaylistSaga);
  yield takeLatest(updatePlaylistAction, updatePlaylistSaga);
  yield takeLatest(deletePlaylist, deletePlaylistSaga);
}

